import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../../../stores/authStore';
import { licenseService } from '../../../services/license';
import { Image, ArrowRight, Award } from 'lucide-react';

const isoPresets = [
  { label: 'ISO 9001', title: 'گواهینامه ISO 9001:2015 - سیستم مدیریت کیفیت', authority: 'Arzyaban Keifiat Khavarmianeh (AKK)', issue_date: '05 February 2024 — معتبر تا 04 February 2027', reference_no: 'QAM-1067-Q', description: 'این گواهینامه به سیستم مدیریت کیفیت مجموعه مربوط است و دامنه درج‌شده در مدرک «تولید مصنوعات طلا و جواهر و ساعت» است. این مدرک نشانه رویکرد سیستماتیک به مدیریت کیفیت است و به معنی تضمین کیفیت تک‌تک محصولات توسط سازمان ISO نیست.' },
  { label: 'ISO 10004', title: 'گواهینامه ISO 10004:2018 - پایش و سنجش رضایت مشتری', authority: 'Arzyaban Keifiat Khavarmianeh (AKK)', issue_date: '05 February 2024 — معتبر تا 04 February 2027', reference_no: 'QAM-0556-CS', description: 'این گواهینامه به چارچوب پایش و سنجش رضایت مشتری مربوط است و دامنه درج‌شده در مدرک «تولید مصنوعات طلا و جواهر و ساعت» است. رضایت مشتری در این چارچوب موضوعی قابل پایش، اندازه‌گیری و بهبود است؛ نه ادعای رضایت صددرصدی.' },
  { label: 'ISO 10668', title: 'گواهینامه ISO 10668:2010 - چارچوب ارزش‌گذاری برند', authority: 'Arzyaban Keifiat Khavarmianeh (AKK)', issue_date: '05 February 2024 — معتبر تا 04 February 2027', reference_no: 'QAM-0120-BV', description: 'این گواهینامه به حوزه ارزش‌گذاری برند مربوط است و دامنه درج‌شده در مدرک «تولید مصنوعات طلا و جواهر و ساعت» است. مدرک، مبلغ یا ارزش مالی مشخصی برای برند راز اعلام یا تضمین نمی‌کند.' },
];

const LicenseFormPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(!!id);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: ''
    ,authority: '', issue_date: '', reference_no: ''
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
      navigate('/dashboard', { replace: true });
      return;
    }
    if (id) {
      loadData(id);
    }
  }, [user, id]);

  const loadData = async (licenseId: string) => {
    try {
      const data = await licenseService.getById(licenseId);
      setFormData({
        title: data.title,
        description: data.description || '',
        image_url: data.image_url,
        authority: data.authority || '', issue_date: data.issue_date || '', reference_no: data.reference_no || ''
      });
    } catch (error) {
      console.error('Error loading license:', error);
      alert('خطا در بارگذاری اطلاعات');
      navigate('/admin/licenses');
    } finally {
      setFetching(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      try {
        setLoading(true);
        const res = await licenseService.uploadImage(file);
        setFormData(prev => ({ ...prev, image_url: res.url }));
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('خطا در آپلود تصویر');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.image_url) {
        alert('عنوان و تصویر الزامی هستند');
        return;
    }

    setLoading(true);
    try {
      if (id) {
        await licenseService.update(id, formData);
      } else {
        await licenseService.create(formData);
      }
      navigate('/admin/licenses');
    } catch (error) {
      console.error('Error saving license:', error);
      alert('خطا در ذخیره اطلاعات');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return <div className="p-8 text-center text-gray-500">در حال بارگذاری...</div>;
  }

  return (
    <div className="p-4 lg:p-8 max-w-3xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
            <button 
                onClick={() => navigate('/admin/licenses')}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
            >
                <ArrowRight className="w-6 h-6" />
            </button>
            <div>
                <h1 className="text-2xl font-bold text-gray-900">
                    {id ? 'ویرایش مجوز' : 'افزودن مجوز جدید'}
                </h1>
            </div>
        </div>

      <div className="card p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {!id && <div className="rounded-xl border border-amber-200 bg-amber-50 p-4"><div className="flex items-center gap-2 text-sm font-semibold text-amber-900"><Award size={18}/>ثبت سریع گواهینامه ISO</div><p className="text-xs text-amber-800/70 mt-2">یکی از الگوها را انتخاب کنید؛ متن حقوقی، مرجع، تاریخ و شماره گواهینامه تکمیل می‌شود.</p><div className="flex flex-wrap gap-2 mt-4">{isoPresets.map((preset)=><button key={preset.label} type="button" onClick={()=>setFormData((prev)=>({...prev,...preset}))} className="px-3 py-2 bg-white border border-amber-200 rounded-lg text-xs text-amber-900">{preset.label}</button>)}</div></div>}
          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              تصویر مجوز *
            </label>
            <div 
                className={`relative border-2 border-dashed rounded-xl p-4 transition-colors ${
                    formData.image_url ? 'border-blue-300 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                }`}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    className="hidden"
                />
                
                {formData.image_url ? (
                    <div className="relative aspect-video w-full max-w-md mx-auto rounded-lg overflow-hidden bg-white shadow-sm">
                        <img 
                            src={formData.image_url} 
                            alt="Preview" 
                            className="w-full h-full object-contain"
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="absolute bottom-2 right-2 bg-black/50 text-white px-3 py-1.5 rounded-md text-xs hover:bg-black/70 transition-colors backdrop-blur-sm"
                        >
                            تغییر تصویر
                        </button>
                    </div>
                ) : (
                    <div 
                        onClick={() => fileInputRef.current?.click()}
                        className="flex flex-col items-center justify-center py-12 cursor-pointer text-gray-500 hover:text-gray-700"
                    >
                        <Image className="w-12 h-12 mb-2" />
                        <span className="text-sm font-medium">برای آپلود تصویر کلیک کنید</span>
                        <span className="text-xs text-gray-400 mt-1">فقط تصویر JPG، PNG یا WebP؛ فایل PDF بارگذاری نکنید</span>
                    </div>
                )}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              عنوان *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="input w-full"
              placeholder="مثال: مجوز اتحادیه طلا و جواهر"
              required
            />
          </div>

          {/* Description */}
          <div className="grid md:grid-cols-3 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-2">مرجع صادرکننده</label><input type="text" value={formData.authority} onChange={(e) => setFormData({ ...formData, authority: e.target.value })} className="input w-full" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-2">تاریخ صدور</label><input type="text" value={formData.issue_date} onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })} className="input w-full" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-2">شماره مجوز</label><input type="text" value={formData.reference_no} onChange={(e) => setFormData({ ...formData, reference_no: e.target.value })} className="input w-full" /></div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              توضیحات
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input w-full h-32 resize-none"
              placeholder="توضیحات تکمیلی در مورد مجوز..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => navigate('/admin/licenses')}
              className="px-6 py-2.5 rounded-xl text-gray-600 hover:bg-gray-100 transition-colors font-medium"
            >
              انصراف
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2.5 rounded-xl bg-[#0e444e] text-white hover:bg-[#0b363e] shadow-lg shadow-[#0e444e]/20 font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LicenseFormPage;
